import { Controller } from "@hotwired/stimulus";

// Live price recalculation for step2. Replaces the old MutationObserver +
// manual DOM-row-injection pattern (packs/desktop/step2.js) with a direct
// recompute: total = the non-toggleable base price (flight/traveler price,
// cfar, climate, discount -- everything booking_summery_lines#total_price
// already included server-side, minus whatever ancillaries were already
// selected at render time, see the totalTarget's data-price-base in
// step2/_summery.html.erb) + sum of any CHECKED ancillary checkboxes
// carrying data-price, scoped to this form.
//
// Also keeps the itemized Prisöversikt line-item list (lineItemsTarget) in
// sync with those same checkboxes -- checking a bag used to update the
// total but leave the breakdown list showing only whatever was selected at
// page load. Every ancillary checkbox this page has (the shared baggage
// panels AND the per-traveler sports-equipment list) already carries (or
// now carries, see _step_1_baggage_panel.html.erb) data-id/data-description
// alongside data-price -- matched against a line row's own
// data-ancillary-row-id (booking_summery_lines' ancillary["id"], the same
// id these checkboxes' data-id is), so a checkbox's row is found whether it
// was server-rendered (already selected at page load) or added by this
// controller a moment ago.
export default class extends Controller {
  static targets = ["total", "lineItems"];

  connect() {
    // Capture whatever currency suffix/format the server already rendered
    // (e.g. "kr", "DKK", "€") instead of hardcoding one -- this page serves
    // multiple gates/currencies.
    if (this.hasTotalTarget) {
      const match = this.totalTarget.textContent.trim().match(/[^\d\s.,]+$/);
      this.currencySuffix = match ? ` ${match[0]}` : "";
      // This used to start recalculate() from a flat 0, which on this
      // very first call (immediately, before any checkbox was ever
      // touched) replaced the server-rendered total -- which correctly
      // included the flight/traveler price -- with one that never added
      // it back in at all. data-price-base is exactly the number that
      // produced that server-rendered total in the first place.
      this.base = Number(this.totalTarget.dataset.priceBase) || 0;
    }
    this.recalculate();
  }

  recalculate() {
    let total = this.base || 0;
    const checkedAncillaries = [];

    this.element.querySelectorAll('input[type="checkbox"][data-price]').forEach((checkbox) => {
      if (checkbox.checked) {
        const price = Number(checkbox.dataset.price) || 0;
        total += price;
        if (checkbox.dataset.id) {
          checkedAncillaries.push({ id: checkbox.dataset.id, description: checkbox.dataset.description, price });
        }
      }
    });

    // totalTargets (plural), not the singular accessor -- the mobile sticky
    // price bar (see step2/_summery.html.erb) is a second element carrying
    // this same target so both stay in sync; the singular accessor would
    // silently only ever update whichever one Stimulus happens to find
    // first.
    this.totalTargets.forEach((el) => {
      el.textContent = this.formatPrice(total);
    });

    this.syncLineItems(checkedAncillaries);
  }

  syncLineItems(checkedAncillaries) {
    if (!this.hasLineItemsTarget) return;
    const checkedIds = new Set(checkedAncillaries.map((a) => a.id));

    // Drop any row -- server-rendered at page load, or added by this
    // controller a moment ago -- for an ancillary that's no longer checked.
    this.lineItemsTarget.querySelectorAll("[data-ancillary-row-id]").forEach((row) => {
      if (!checkedIds.has(row.dataset.ancillaryRowId)) row.remove();
    });

    // Add a row for any checked ancillary that doesn't already have one
    // (nothing to do if it was already selected server-side).
    checkedAncillaries.forEach(({ id, description, price }) => {
      if (this.lineItemsTarget.querySelector(`[data-ancillary-row-id="${CSS.escape(id)}"]`)) return;

      // Byte-for-byte the markup of front/common/_price_row.html.erb, which
      // renders every other row in every breakdown in the funnel. It has to
      // be: this row is added the instant a bag is ticked, and the server
      // re-renders the very same line on the next page load and on step3/
      // step4 -- if the classes drift, the same item visibly changes shape
      // between being chosen and being bought. The old version wrapped the
      // amount in an extra flex span and gave the label no min-w-0/flex-1,
      // so a long name wrapped differently here than everywhere else.
      const row = document.createElement("div");
      row.className = "flex items-center justify-between gap-3 py-2 text-gray-700";
      row.dataset.ancillaryRowId = id;

      const label = document.createElement("span");
      label.className = "min-w-0 flex-1";
      label.textContent = description || "";

      const totalEl = document.createElement("span");
      totalEl.className = "shrink-0 whitespace-nowrap font-medium text-gray-900";
      totalEl.textContent = this.formatPrice(price);

      row.appendChild(label);
      row.appendChild(totalEl);
      this.lineItemsTarget.appendChild(row);
    });
  }

  formatPrice(amount) {
    return `${Math.round(amount).toLocaleString("sv-SE")}${this.currencySuffix || ""}`;
  }
}
